/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { Fragment } from 'react';
import { Transition } from '@headlessui/react';
import { find } from 'lodash';
import cx from 'classnames';

import { toggleShowDetails } from '@broadleaf/admin-components/dist/form/item-browser/actions/itemBrowser';
import ItemBrowserDetails from '@broadleaf/admin-components/dist/form/item-browser/components/ItemBrowserDetails';
import ItemBrowserFolder from '@broadleaf/admin-components/dist/form/item-browser/components/ItemBrowserFolder';
import ItemBrowserGrid from '../ItemBrowserGrid';
import useConnectItemBrowser from '@broadleaf/admin-components/dist/form/item-browser/hooks/useConnectItemBrowser';
import SplitPane from 'react-split-pane';
import type { IMetadataAction } from '@broadleaf/admin-components/dist/types/metadata';

/**
 * This renders the ListGrid or TileGrid displaying the actual items inside an
 * ItemBrowser through use of ItemBrowserGrid. It also renders the item details
 * if enabled and selected, and supports rendering the folder tree display.
 */
export const ItemBrowserBody: React.FC<ItemBrowserBodyProps> = props => {
  const { actions, state } = useConnectItemBrowser({
    toggleShowDetails
  });
  const detailsActionDefinition = find(props.actionDefinitions, {
    placement: 'DETAILS',
    type: 'UPDATE'
  });

  const PaneComponent = props.enableFolders ? SplitPane : Fragment;
  return (
    <div className="ItemBrowserBody tw-relative tw-mx-auto tw-my-0 tw-flex tw-w-full tw-flex-auto tw-flex-row tw-flex-nowrap tw-overflow-x-hidden tw-rounded-sm tw-border-b tw-border-l tw-border-r tw-border-solid tw-border-gray-200">
      <PaneComponent
        {...getSplitPaneProps({
          showFolders: state.foldersUI.showFolders,
          enableFolders: props.enableFolders
        })}
      >
        <ItemBrowserFolder {...props} />
        <Fragment>
          <ItemBrowserGrid
            altKey={props.altKey}
            embedCodeKey={props.embedCodeKey}
            emptyMessage={props.emptyMessage}
            enableUploads={props.enableUploads}
            idKey={props.idKey}
            mimeTypeKey={props.mimeTypeKey}
            primaryKey={props.primaryKey}
            rowComponent={props.rowComponent}
            rowHeaderComponent={props.rowHeaderComponent}
            rowProps={props.rowProps}
            srcKey={props.srcKey}
            tagsKey={props.tagsKey}
            tileComponent={props.tileComponent}
            tileProps={props.tileProps}
            titleKey={props.titleKey}
            typeKey={props.typeKey}
          />
          {props.enableDetails && (
            <TransitionItemBrowserDetails
              actionDefinition={detailsActionDefinition}
              altKey={props.altKey}
              embedCodeKey={props.embedCodeKey}
              enableDetailsUpdates={props.enableDetailsUpdates}
              item={
                state.itemsUI.selectedId
                  ? state.entities.items[state.itemsUI.selectedId]
                  : null
              }
              mimeTypeKey={props.mimeTypeKey}
              onClose={() => actions.toggleShowDetails(false)}
              onSubmitForm={props.handleUpdate}
              showDetails={state.itemsUI.showDetails}
              primaryKey={props.primaryKey}
              srcKey={props.srcKey}
              titleKey={props.titleKey}
              typeKey={props.typeKey}
            />
          )}
        </Fragment>
      </PaneComponent>
    </div>
  );
};

ItemBrowserBody.defaultProps = {
  altKey: 'alt',
  embedCodeKey: 'embedCode',
  emptyMessage: 'Drop files here to upload',
  enableDetails: true,
  enableDetailsUpdates: true,
  enableFolders: false,
  enableUploads: true,
  idKey: 'id',
  mimeTypeKey: 'mimeType',
  primaryKey: 'primary',
  srcKey: 'url',
  tagsKey: 'tags',
  titleKey: 'title',
  typeKey: 'type'
};

export interface ItemBrowserBodyProps {
  /** The actions for either the filters, item icon bar, global icon bar, or other action components. */
  actionDefinitions?: Array<IMetadataAction>;
  /** The property key for getting an item's alt value */
  altKey?: string;
  /** The property key for getting an item's embed code value. This is used for item's without a source url and instead contain embedded content.  */
  embedCodeKey?: string;
  emptyMessage?: string;
  /** Enables the details view to display when an item is selected. */
  enableDetails?: boolean;
  /** Enables the details view to render a form that allow updates */
  enableDetailsUpdates?: boolean;
  /** Enables the folder view to display. */
  enableFolders?: boolean;
  enableUploads?: boolean;
  /** Handles the updating of an item from the ItemBrowserDetails component */
  handleUpdate: Function;
  /** The property key for getting an item's id value */
  idKey?: string;
  /** The property key for getting an item's mimeType value */
  mimeTypeKey?: string;
  /** The property key for getting an item's primary value, if it exists */
  primaryKey?: string;
  /**
   * The component used for rendering the row. Typically this will default to
   * a 'tr' HTML element if not provided.
   */
  rowComponent?: React.ReactNode;
  /**
   * The component used for rendering the row header. Typically this will default
   * to a 'tr' HTML element if not provided.
   */
  rowHeaderComponent: React.ReactNode;
  /**
   * Any additional props to pass down to each row component. This is useful for
   * passing down shared props that may not be resident on each row's data.
   */
  rowProps: object;
  /** The property key for getting an item's src value */
  srcKey: string;
  /** The property key for getting an item's tags value */
  tagsKey: string;
  /**
   * The component used for rendering the tile. Typically this will default to
   * a `ItemBrowserThumbnailTile` component if not provided.
   */
  tileComponent: React.ReactNode;
  /**
   * Any additional props to pass down to each tile component. This is useful for
   * passing down shared props that may not be resident on each item's data.
   */
  tileProps: object;
  /** The property key for getting an item's title value */
  titleKey: string;
  /** The property key for getting an item's type value */
  typeKey: string;
}

const TransitionItemBrowserDetails = props => {
  return (
    <Transition
      show={props.item != null && props.showDetails}
      enter="tw-transition-all tw-transform tw-duration-300 tw-ease-in tw-origin-right motion-reduce:transition-none motion-reduce:transform-none"
      enterFrom="tw-translate-x-full tw-overflow-hidden"
      enterTo="tw-translate-x-0"
      leave="tw-transition-all tw-transform tw-duration-300 tw-ease-out tw-origin-right motion-reduce:transition-none motion-reduce:transform-none"
      leaveFrom="tw-translate-x-0"
      leaveTo="tw-translate-x-full tw-overflow-hidden"
    >
      <ItemBrowserDetails
        className="flex-grow-0 flex-shrink-0 ItemBrowserBody__ItemBrowserDetails"
        {...props}
      />
    </Transition>
  );
};

const getSplitPaneProps = ({ showFolders, enableFolders }) => {
  if (!enableFolders) {
    return {};
  }

  const splitPaneProps = {
    defaultSize: 300,
    maxSize: 600,
    minSize: 100,
    pane1ClassName:
      'ItemBrowserBody__pane ItemBrowserBody__pane--left tw-h-full tw-bg-gray-100 tw-bg-white tw-border-b-gray-200 tw-border-r-gray-200 tw-border-b tw-border-r tw-border-solid hover:tw-opacity-100 hover:tw-transition-all hover:tw-duration-500 hover:tw-ease-in-out tw-shadow',
    pane2ClassName:
      'ItemBrowserBody__pane ItemBrowserBody__pane--right tw-h-full tw-bg-gray-100 tw-flex tw-flex-row tw-flex-nowrap tw-overflow-x-hidden',
    resizerClassName: cx(
      'ItemBrowserBody__resizer',
      'tw-w-3 tw-my-0 tw-bg-clip-padding tw-border-solid tw-opacity-20 tw-box-border tw--mx-2 tw-z-[1] tw-bg-black tw-border-r-2 tw-border-l-2 tw-border-transparent hover:tw-border-gray-50 tw-cursor-[col-resize] hover:tw-transition-all hover:tw-duration-[2s] hover:tw-ease',
      {
        'd-none': !showFolders
      }
    ),
    size: 300,
    split: 'vertical',
    pane1Style: undefined
  };

  if (!showFolders) {
    // we set the max width to 0 to transition from "open" to "closed"
    splitPaneProps.pane1Style = {
      maxWidth: 0,
      transition: 'max-width .4s ease-in-out'
    };
  } else {
    // we set the max width to 400 again to transition from "closed" to "open"
    splitPaneProps.pane1Style = {
      maxWidth: 600,
      transition: 'max-width .4s ease-in-out'
    };
  }

  return splitPaneProps;
};

export default ItemBrowserBody;
